import { motion } from "framer-motion";
import { MessageCircle, Phone } from "lucide-react";
import { PRIORITY_COUNTRIES } from "@/data/content";
import { CONTACT } from "@/data/content";

export default function PriorityCountries() {
  return (
    <section
      id="priority-countries"
      data-testid="priority-countries-section"
      className="border-b border-[#E4E7EC] bg-white"
    >
      <div className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-28">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <p className="overline mb-4">§ 05a — Priority lanes</p>
            <h2 className="font-display text-4xl font-black leading-[0.95] tracking-tight text-[#101828] sm:text-5xl md:text-6xl">
              Nine lanes we <span className="text-[#D92D20]">actively invest</span> in.
            </h2>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-[#101828]">
              <strong>Express company partner program:</strong> we now operate
              on-ground clearance desks in <strong>India, the UK (LHR), and
              South Korea (ICN)</strong> under one MSA — read the{" "}
              <a
                href="/express-clearance-partner-india"
                className="text-[#D92D20] underline hover:no-underline"
                data-testid="express-partner-link"
              >
                Express Partner page
              </a>{" "}
              or the{" "}
              <a
                href="/blog/express-clearance-partner-india-uk-south-korea"
                className="text-[#D92D20] underline hover:no-underline"
              >
                full brief
              </a>
              .
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <a
                href={CONTACT.whatsappHref}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="priority-whatsapp"
                className="inline-flex items-center justify-center gap-2 border-2 border-[#101828] bg-[#25D366] px-5 py-3 text-sm font-semibold text-[#0B111A] shadow-hard hover:bg-[#1FBE5A]"
              >
                <MessageCircle className="h-4 w-4" strokeWidth={2.5} /> WhatsApp our lane manager
              </a>
              <a
                href={CONTACT.telHref}
                data-testid="priority-call"
                className="inline-flex items-center justify-center gap-2 border-2 border-[#101828] bg-white px-5 py-3 text-sm font-semibold text-[#101828] hover:bg-[#101828] hover:text-white"
              >
                <Phone className="h-4 w-4" strokeWidth={2.5} /> {CONTACT.phoneDisplay}
              </a>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="grid grid-cols-1 gap-0 border-2 border-[#101828] md:grid-cols-3">
              {PRIORITY_COUNTRIES.map((c, i) => {
                const col = i % 3;
                const lastRowStart = PRIORITY_COUNTRIES.length - (PRIORITY_COUNTRIES.length % 3 || 3);
                return (
                  <motion.article
                    key={c.code}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.4, delay: i * 0.04 }}
                    className={`bg-[#F9FAFB] p-5 ${
                      col !== 2 ? "md:border-r md:border-[#101828]" : ""
                    } ${i < lastRowStart ? "md:border-b md:border-[#101828]" : ""} ${
                      i < PRIORITY_COUNTRIES.length - 1 ? "border-b border-[#E4E7EC] md:border-b" : ""
                    }`}
                    data-testid={`priority-country-${c.code}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-2xl leading-none" aria-hidden="true">
                        {c.flag}
                      </span>
                      <div>
                        <div className="font-display text-lg font-bold tracking-tight text-[#101828]">
                          {c.name}
                        </div>
                        <div className="font-mono-accent text-[#D92D20]">{c.code}</div>
                      </div>
                    </div>
                    <div className="mt-3 font-mono-accent text-[10px] text-[#475467]">
                      {c.lanes}
                    </div>
                    <p className="mt-3 text-xs leading-relaxed text-[#475467]">
                      {c.intent}
                    </p>
                  </motion.article>
                );
              })}
            </div>
            <p className="mt-6 text-xs leading-relaxed text-[#475467]">
              <strong className="text-[#101828]">Search-friendly aliases we rank for:</strong>{" "}
              {PRIORITY_COUNTRIES.map((c) => c.keywords).join(" · ")}.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
